import React, { useState, useRef, useEffect } from 'react';
import { Link, NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { IMAGE_URL } from '../Constants/constants';
import WalletButton from '../chain/WalletButton';
import BpLogo from './BpLogo';

const NAV_LINKS = [
    { to: '/search', label: 'Shops' },
    { to: '/products', label: 'Products' },
    { to: '/deals', label: 'Deals' },
    { to: '/news', label: 'News' },
];

export default function Header() {
    const navigate = useNavigate();
    const location = useLocation();
    const user = useSelector(state => state.user);
    const cartItems = useSelector(state => state.products);
    const [menuOpen, setMenuOpen] = useState(false);
    const [profileOpen, setProfileOpen] = useState(false);
    const [query, setQuery] = useState('');
    const profileRef = useRef(null);

    const cartCount = (cartItems || []).reduce((n, i) => n + (parseInt(i.quantity, 10) || 1), 0);
    const loggedIn = !!(user.email || user.walletAddress || user._id);

    // close menus on navigation
    useEffect(() => { setMenuOpen(false); setProfileOpen(false); }, [location]);

    // close profile dropdown on outside click
    useEffect(() => {
        const onDoc = (e) => { if (profileRef.current && !profileRef.current.contains(e.target)) setProfileOpen(false); };
        document.addEventListener('mousedown', onDoc);
        return () => document.removeEventListener('mousedown', onDoc);
    }, []);

    // lock body scroll while the mobile menu is open
    useEffect(() => {
        document.body.style.overflow = menuOpen ? 'hidden' : '';
        return () => { document.body.style.overflow = ''; };
    }, [menuOpen]);

    const submitSearch = (e) => {
        e.preventDefault();
        setMenuOpen(false);
        navigate('/search', { state: { sr: query } });
    };

    const avatar = user.image ? IMAGE_URL + user.image : null;

    const profileMenu = (
        <div className="bp-profile-menu">
            <div className="bp-profile-menu-head">
                <span className="bp-profile-name">{user.username || 'Account'}</span>
                <span className="bp-profile-email">{user.email || (user.walletAddress ? user.walletAddress.slice(0, 10) + "…" + user.walletAddress.slice(-6) : "")}</span>
            </div>
            <Link to="/profile" className="bp-profile-item"><i className="fa fa-user" /> Your Profile</Link>
            {user.role === 'SELLER' && (
                <Link to="/dashboard" className="bp-profile-item"><i className="fa fa-th-large" /> Seller Dashboard</Link>
            )}
            <Link to="/profile/pre-orders" className="bp-profile-item"><i className="fa fa-cube" /> Your Orders</Link>
            <button className="bp-profile-item danger" onClick={() => { window.location.replace('/logout') }}>
                <i className="fa fa-sign-out" /> Logout
            </button>
        </div>
    );

    return (
        <header className="bp-header">
            <div className="bp-header-inner">

                {/* mobile hamburger */}
                <button
                    className={`bp-burger ${menuOpen ? 'open' : ''}`}
                    aria-label={menuOpen ? 'Close menu' : 'Open menu'}
                    onClick={() => setMenuOpen(!menuOpen)}>
                    <span /><span /><span />
                </button>

                {/* brand */}
                <Link to="/" className="bp-brand" aria-label="BirrPlug home">
                    <BpLogo size={30} />
                </Link>

                {/* desktop nav */}
                <nav className="bp-nav">
                    {NAV_LINKS.map(l => (
                        <NavLink key={l.to} to={l.to} className={({ isActive }) => `bp-nav-link ${isActive ? 'active' : ''}`}>
                            {l.label}
                        </NavLink>
                    ))}
                </nav>

                {/* desktop search */}
                <form className="bp-header-search" onSubmit={submitSearch} role="search">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" aria-hidden="true">
                        <circle cx="11" cy="11" r="7" /><path d="m21 21-4.3-4.3" />
                    </svg>
                    <input
                        value={query}
                        onChange={e => setQuery(e.target.value)}
                        placeholder="Search shops, products…"
                        aria-label="Search"
                    />
                </form>

                {/* right cluster */}
                <div className="bp-header-actions">
                    <Link to="/cart" className="bp-cart-btn" aria-label={`Cart, ${cartCount} items`}>
                        <i className="fa fa-shopping-cart" />
                        {cartCount > 0 && <span className="bp-cart-badge">{cartCount > 99 ? '99+' : cartCount}</span>}
                    </Link>

                    <div className="bp-header-wallet"><WalletButton /></div>

                    {loggedIn ? (
                        <div className="bp-profile" ref={profileRef}>
                            <button className="bp-avatar-btn" onClick={() => setProfileOpen(!profileOpen)} aria-label="Account menu">
                                {avatar
                                    ? <img src={avatar} alt="" className="bp-avatar" />
                                    : <span className="bp-avatar bp-avatar-fallback">{(user.username || 'U').charAt(0).toUpperCase()}</span>}
                                <i className={`fa fa-angle-down bp-avatar-caret ${profileOpen ? 'up' : ''}`} />
                            </button>
                            {profileOpen && profileMenu}
                        </div>
                    ) : (
                        <div className="bp-auth-btns">
                            <Link to="/login" className="bp-login-btn">Login</Link>
                            <Link to="/join" className="bp-join-btn">Join</Link>
                        </div>
                    )}
                </div>
            </div>

            {/* mobile slide-down menu */}
            <div className={`bp-mobile-menu ${menuOpen ? 'open' : ''}`}>
                <form className="bp-mobile-search" onSubmit={submitSearch} role="search">
                    <input
                        value={query}
                        onChange={e => setQuery(e.target.value)}
                        placeholder="Search shops, products…"
                        aria-label="Search"
                    />
                    <button type="submit" className="bp-btn-primary">Search</button>
                </form>
                <nav className="bp-mobile-nav">
                    {NAV_LINKS.map(l => (
                        <NavLink key={l.to} to={l.to} className={({ isActive }) => `bp-mobile-link ${isActive ? 'active' : ''}`}>
                            {l.label}
                        </NavLink>
                    ))}
                    <NavLink to="/join" className="bp-mobile-link">Sell on BirrPlug</NavLink>
                </nav>
                <div className="bp-mobile-menu-foot">
                    <WalletButton />
                    {loggedIn ? (
                        <div className="bp-mobile-account">
                            <Link to="/profile" className="bp-mobile-link"><i className="fa fa-user" /> {user.username || 'Profile'}</Link>
                            {user.role === 'SELLER' && <Link to="/dashboard" className="bp-mobile-link"><i className="fa fa-th-large" /> Seller Dashboard</Link>}
                            <Link to="/profile/pre-orders" className="bp-mobile-link"><i className="fa fa-cube" /> Your Orders</Link>
                            <button className="bp-mobile-link danger" onClick={() => { window.location.replace('/logout') }}><i className="fa fa-sign-out" /> Logout</button>
                        </div>
                    ) : (
                        <div className="bp-mobile-auth">
                            <Link to="/login" className="bp-btn-ghost">Login</Link>
                            <Link to="/join" className="bp-btn-primary">Create Account</Link>
                        </div>
                    )}
                </div>
            </div>
            {menuOpen && <div className="bp-mobile-backdrop" onClick={() => setMenuOpen(false)} />}
        </header>
    );
}
